import { createStore, type Store, type StoreActionApi } from 'react-sweet-state';

import type {
	AlphaExtendedThinkingMode,
	AutoActionApprovalMode,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type RovoControlsSnapshot } from './ChatModeInternal';
import { getAppFilters } from './getAppFilters';
import { getDerivedDefaultChatMode } from './getDerivedDefaultChatMode';
import { getDerivedReasoningMode } from './getDerivedReasoningMode';
import type {
	RovoApp,
	OrgKnowledge,
	ReasoningMode,
	SourceMode,
	ChatMode,
	ChatModeState,
	WebSearchDefaultOptions,
} from './types';
import { updateMode } from './updateMode';

const getDefaultChatMode = (options?: WebSearchDefaultOptions) => {
	const defaultChatMode = getDerivedDefaultChatMode();

	if (options?.webSearchDefaultEnabled === undefined) {
		return defaultChatMode;
	}

	return {
		...defaultChatMode,
		webSearchEnabled: options.webSearchDefaultEnabled,
	};
};

const actions = {
	setAutoActionApprovalMode:
		(
			key: string,
			autoActionApprovalMode: AutoActionApprovalMode,
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { autoActionApprovalMode }));
		},
	setReasoningMode:
		(
			key: string,
			reasoningMode: ReasoningMode,
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { reasoningMode }));
		},
	resetReasoningMode:
		(key: string): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { reasoningMode: getDerivedDefaultChatMode().reasoningMode }));
		},
	setDeepResearchEnabled:
		(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(
				updateMode(key, {
					reasoningMode: isEnabled ? 'deep-research' : 'auto',
				}),
			);
		},
	setWebSearchEnabled:
		(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { webSearchEnabled: isEnabled, webSearchEnabledOverridden: true }));
		},
	setConciergeEnabled:
		(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { conciergeEnabled: isEnabled }));
		},
	seedWebSearchDefault:
		(
			key: string,
			webSearchDefaultEnabled: boolean,
		): (({ dispatch, getState }: StoreActionApi<ChatModeState>) => boolean) =>
		({ dispatch, getState }) => {
			const state = getState()[key];
			if (state?.webSearchEnabledOverridden) {
				return false;
			}
			if (state?.rovoControlsSnapshot) {
				dispatch(
					updateMode(key, {
						rovoControlsSnapshot: {
							...state.rovoControlsSnapshot,
							webSearchEnabled: webSearchDefaultEnabled,
						},
					}),
				);
				return false;
			}
			dispatch(updateMode(key, { webSearchEnabled: webSearchDefaultEnabled }));
			return true;
		},
	setSourceMode:
		(
			key: string,
			sourceMode: SourceMode,
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { sourceMode }));
		},
	setOrgKnowledge:
		(
			key: string,
			orgKnowledge: OrgKnowledge,
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { orgKnowledge, appFiltersSetByPrompt: false }));
		},
	setAppFiltersFromPrompt:
		(
			key: string,
			filteredApps: RovoApp[],
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(
				updateMode(key, {
					orgKnowledge: { filteredApps },
					appFiltersSetByPrompt: true,
				}),
			);
		},
	addAppFilter:
		(
			key: string,
			app: RovoApp,
		): (({ getState, dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ getState, dispatch }) => {
			const { orgKnowledge } = getState()[key] || getDerivedDefaultChatMode();
			// An empty filter means "all connected apps apply" (see getAppFilters), so a
			// just-connected app is already included. Writing it in explicitly would turn
			// "all apps" into "only this app" and silently drop the rest — leave it be. Same
			// for 'disabled' (org knowledge off entirely). Only a narrowed filter needs the
			// app appended so it isn't excluded from the next message.
			if (orgKnowledge === 'disabled' || orgKnowledge.filteredApps.length === 0) {
				return;
			}
			if (orgKnowledge.filteredApps.some((a) => a.source === app.source)) {
				return;
			}
			// Preserve appFiltersSetByPrompt (don't force it true): appending on connect must
			// not turn a manually-narrowed filter into a prompt-set one, which the input-clear
			// handler (RovoChatPromptInput) would then discard back to "all apps".
			dispatch(
				updateMode(key, {
					orgKnowledge: { filteredApps: [...orgKnowledge.filteredApps, app] },
				}),
			);
		},
	setUseCurrentPageContext:
		(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { useCurrentPageContext: isEnabled }));
		},
	setSpaceSourceCount:
		(key: string, count?: number): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { spaceSourceCount: count }));
		},
	setBrowserDisplayContextActive:
		(key: string, active: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { browserDisplayContextActive: active }));
		},
	getBrowserDisplayContextActive:
		(key: string): (({ getState }: StoreActionApi<ChatModeState>) => boolean) =>
		({ getState }) =>
			getState()[key]?.browserDisplayContextActive === true,
	setSelectedCloudIds:
		(
			key: string,
			selectedCloudIds: string[],
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			dispatch(updateMode(key, { selectedCloudIds }));
		},
	setChatMode:
		(
			key: string,
			chatMode: Partial<
				Omit<
					ChatMode,
					'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled' | 'fastModeEnabled'
				>
			>,
		): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch }) => {
			let updatedChatMode: Partial<ChatMode> = chatMode;
			let orgKnowledge: OrgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
			if (chatMode.appFilters === undefined) {
				orgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
			} else if (chatMode.appFilters.length > 0) {
				orgKnowledge = {
					filteredApps: chatMode.appFilters,
				};
			} else {
				orgKnowledge = 'disabled';
			}
			updatedChatMode = {
				...chatMode,
				orgKnowledge: orgKnowledge,
				...(chatMode.webSearchEnabled !== undefined ? { webSearchEnabledOverridden: true } : {}),
			};
			dispatch(updateMode(key, { ...updatedChatMode, appFiltersSetByPrompt: false }));
		},
	getChatMode:
		(
			key: string,
		): (({ getState }: StoreActionApi<ChatModeState>) => {
			appFilters: RovoApp[] | undefined;
			selectedCloudIds: string[];
			appFiltersSetByPrompt: boolean;
			deepResearchEnabled: boolean;
			thinkDeeperEnabled: boolean;
			fastModeEnabled: boolean;
			autoReasoningEnabled: boolean;
			alphaExtendedThinkingMode: AlphaExtendedThinkingMode | undefined;
			autoActionApprovalMode: AutoActionApprovalMode;
			taskModeEnabled: boolean;
			reasoningMode: ReasoningMode;
			webSearchEnabled: boolean;
			conciergeEnabled: boolean;
			sourceMode: SourceMode;
			orgKnowledge: OrgKnowledge;
			autoApplyEnabled: boolean;
			useCurrentPageContext: boolean;
			spaceSourceCount?: number;
		}) =>
		({ getState }) => {
			const state = getState()[key] || getDerivedDefaultChatMode();
			return {
				...state,
				...getDerivedReasoningMode(state.reasoningMode),
				appFilters: getAppFilters(state.orgKnowledge),
			};
		},
	clearMode:
		(
			key: string,
			options?: WebSearchDefaultOptions,
		): (({ dispatch, getState }: StoreActionApi<ChatModeState>) => void) =>
		({ dispatch, getState }) => {
			const currentMode = getState()[key];
			const shouldPreserveWebSearchEnabled =
				options?.preserveWebSearchEnabled === true &&
				currentMode?.webSearchEnabledOverridden === true;
			const shouldPreserveUseCurrentPageContext =
				options?.preserveUseCurrentPageContext === true &&
				currentMode?.useCurrentPageContext === false;
			const defaultChatMode = getDefaultChatMode(options);

			dispatch(
				updateMode(key, {
					...defaultChatMode,
					webSearchEnabled: shouldPreserveWebSearchEnabled
						? currentMode.webSearchEnabled
						: defaultChatMode.webSearchEnabled,
					webSearchEnabledOverridden: shouldPreserveWebSearchEnabled,
					useCurrentPageContext: shouldPreserveUseCurrentPageContext
						? currentMode.useCurrentPageContext
						: defaultChatMode.useCurrentPageContext,
				}),
			);
		},
	/**
	 * Keeps the Rovo-owned controls (reasoning mode, web search, app filters) in
	 * sync with who owns the next turn, and is the single owner of the
	 * snapshot/restore state.
	 *
	 * The stored `rovoControlsSnapshot` doubles as the edge-detector so callers
	 * can invoke this idempotently every render without tracking previous state:
	 *  - `suppressed` with no snapshot: a specialist just took the turn — stash the
	 *    current controls and reset them to defaults so the specialist's own
	 *    reasoning/sources apply.
	 *  - not `suppressed` with a snapshot: the turn returned to Rovo — restore the
	 *    stashed controls and clear the snapshot.
	 *  - otherwise: no-op.
	 *
	 * Returns `RovoControlsSnapshot | null`: the effective control values it applied
	 * (or `null` when nothing changed) so the caller can forward them to its
	 * `on*Change` props. On the reset path `appFilters` is `[]`; on the restore path
	 * it is the stashed value, which may be `undefined` (meaning "no filter / all
	 * apps"). Callers coalesce with `?? []` before calling `onFilteredAppsChange`.
	 */
	setRovoControlsSuppressed:
		(
			key: string,
			{ suppressed }: { suppressed: boolean },
		): (({ dispatch, getState }: StoreActionApi<ChatModeState>) => RovoControlsSnapshot | null) =>
		({ dispatch, getState }) => {
			const state = getState()[key] || getDerivedDefaultChatMode();
			const snapshot = state.rovoControlsSnapshot ?? null;

			if (suppressed && snapshot === null) {
				const derived = {
					...state,
					...getDerivedReasoningMode(state.reasoningMode),
					appFilters: getAppFilters(state.orgKnowledge),
				};
				dispatch(
					updateMode(key, {
						rovoControlsSnapshot: {
							reasoningMode: state.reasoningMode,
							autoActionApprovalMode: state.autoActionApprovalMode,
							webSearchEnabled: derived.webSearchEnabled,
							sourceMode: state.sourceMode,
							appFilters: derived.appFilters,
							selectedCloudIds: state.selectedCloudIds,
						},
						reasoningMode: getDerivedDefaultChatMode().reasoningMode,
						autoActionApprovalMode: 'ALWAYS_HUMAN',
						webSearchEnabled: false,
						sourceMode: getDerivedDefaultChatMode().sourceMode,
						orgKnowledge: getDerivedDefaultChatMode().orgKnowledge,
						selectedCloudIds: [],
						appFiltersSetByPrompt: false,
					}),
				);
				return {
					reasoningMode: getDerivedDefaultChatMode().reasoningMode,
					autoActionApprovalMode: 'ALWAYS_HUMAN',
					webSearchEnabled: false,
					sourceMode: getDerivedDefaultChatMode().sourceMode,
					appFilters: [],
					selectedCloudIds: [],
				};
			}

			if (!suppressed && snapshot !== null) {
				dispatch(
					updateMode(key, {
						rovoControlsSnapshot: null,
						reasoningMode: snapshot.reasoningMode,
						autoActionApprovalMode: snapshot.autoActionApprovalMode,
						webSearchEnabled: snapshot.webSearchEnabled,
						sourceMode: snapshot.sourceMode,
						orgKnowledge:
							snapshot.appFilters === undefined
								? getDerivedDefaultChatMode().orgKnowledge
								: snapshot.appFilters.length > 0
									? { filteredApps: snapshot.appFilters }
									: 'disabled',
						selectedCloudIds: snapshot.selectedCloudIds,
						appFiltersSetByPrompt: false,
					}),
				);
				return snapshot;
			}

			return null;
		},
};

export const store: Store<ChatModeState, typeof actions> = createStore({
	initialState: {} as ChatModeState,
	actions,
});
